// No schema library here: every creator page's script reaches these through its links
// (site/start/lib/links.ts). The content contract's tag schema is in ./index.ts.

/** The most tags a post carries, and the most characters in one. */
export const postTagLimit = 8;
export const postTagLength = 40;

export const tidyPostTag = (tag: string) => tag.replace(/\s+/g, " ").trim();

/** One tag however it's spelled: "storm  mage" and "Storm Mage" are the same tag. */
export const postTagKey = (tag: string) => tidyPostTag(tag).toLowerCase();

/**
 * Tags as someone typed or imported them: spaces tidied, empty and repeated ones dropped (the
 * first spelling stays), each cut to 40 characters and at most 8 in all.
 */
export function postTags(values: readonly string[]) {
  const tags = new Map<string, string>();
  for (const value of values) {
    const tag = tidyPostTag(tidyPostTag(value).slice(0, postTagLength));
    if (tag && !tags.has(postTagKey(tag))) tags.set(postTagKey(tag), tag);
  }
  return [...tags.values()].slice(0, postTagLimit);
}

/** Whether a post has a tag, whatever its case. Posts saved before tags have none. */
export const hasPostTag = (item: { tags?: readonly string[] | null }, tag: string) =>
  !!item.tags?.some((own) => postTagKey(own) === postTagKey(tag));

export type PostTagCount = { tag: string; count: number };

/**
 * Every tag the posts use with how many use it: most used first, then by name. A tag spelled
 * two ways counts once, under the spelling of the first post that has it.
 */
export function postTagCounts(items: readonly { tags?: readonly string[] | null }[]) {
  const counts = new Map<string, PostTagCount>();
  for (const item of items)
    for (const tag of postTags(item.tags ?? [])) {
      const entry = counts.get(postTagKey(tag));
      if (entry) entry.count++;
      else counts.set(postTagKey(tag), { tag, count: 1 });
    }
  return [...counts.values()].sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag));
}
